import React from 'react';
import { SystemText } from 'e4shared';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import Bullet from 'components/prism/bullet';
import ProgressBar from 'components/prism/progressBar';
import Rating from 'components/prism/rating';
import Subheader from 'components/prism/subheader';
import { translateCompetency } from 'helpers/translations';

export const PathwayDetails = ({ data, hideBullet, managerView }) => {
    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);

    const text = React.useMemo(
        () => ({
            title: SystemText('PathwaysDetail/Title'),
            related: SystemText('PathwaysDetail/RelatedTo'),
            requirements: SystemText('PathwaysDetail/Requirements'),
            table: {
                behaviour: SystemText('PathwaysDetail/Table/Behaviour'),
                min: SystemText('PathwaysDetail/Table/Min'),
                your: SystemText('PathwaysDetail/Table/Your'),
            },
            steps: SystemText('PathwaysDetail/Steps'),
            side: {
                progress: SystemText('PathwaysDetail/Progress'),
                due: SystemText('PathwaysDetail/DueBy'),
                status: SystemText('PathwaysDetail/Status'),
                time: SystemText('PathwaysDetail/LearningTime'),
            },
            buttons: {
                resume: SystemText('General/Resume'),
                start: SystemText('General/Start'),
                review: SystemText('General/ReviewCourse'),
            },
        }),
        [user]
    );

    const pathwayStatus = () => {
        if (data.pathway.completed) {
            return 'completed';
        }

        if (data.pathway.progress > 0) {
            return 'in_progress';
        }

        return 'outstanding';
    };

    return (
        <>
            <div className={prismMultiColumn ? 'two-col-66' : ''}>
                <div className="">
                    {!hideBullet && (
                        <div className="flex flex-center mb20">
                            <div className="flex-grow">
                                <Bullet type="pathway" />
                            </div>
                        </div>
                    )}
                    <h1 className="mb10">{data.pathway.name}</h1>
                    {data.pathway.description && <p className="mb10">{data.pathway.description}</p>}
                    {data.behaviours.length > 0 && (
                        <>
                            <h3 className="mb10 mt20">{text.related}</h3>
                            <div className="bullet-list">
                                {data.behaviours.map(behaviour => {
                                    return (
                                        <Link key={`metric-${behaviour.id}`} to={`/behaviour/${behaviour.id}`}>
                                            <Bullet value={behaviour.name} style="blue" />
                                        </Link>
                                    );
                                })}
                            </div>
                        </>
                    )}
                    {data.requirements.length > 0 && (
                        <>
                            <h3 className="mb10 mt20">{text.requirements}</h3>
                            <div className="inline-block">
                                <table className="prism-data-table mb20">
                                    <thead>
                                        <tr>
                                            <th>{text.table.behaviour}</th>
                                            <th className="text-center no-wrap">{text.table.min}</th>
                                            <th className="text-center no-wrap">{text.table.your}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.requirements.map(c => {
                                            c = translateCompetency(user.language_Code, c);
                                            return (
                                                <tr key={`competency-${c.id}`}>
                                                    <td>{c.name}</td>
                                                    <td className="text-center">
                                                        <Rating level={c.target} />
                                                    </td>
                                                    <td className="text-center">
                                                        <Rating level={c.level} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
                <div className={`prism-content-box faded ${!prismMultiColumn ? 'mt20' : ''}`}>
                    <div className="prism-content">
                        {data.steps.length > 0 && (
                            <>
                                <h6>{text.side.progress}</h6>
                                <ProgressBar progress={data.pathway.progress} />
                            </>
                        )}
                        <h6 className="mt10">{text.side.status}</h6>
                        <div>
                            <Bullet isStatus value={pathwayStatus()} />
                        </div>
                        <h6 className="mt20">{text.side.due}</h6>
                        <div>
                            {data.pathway.completed ? (
                                <Bullet isDueDate value={data.pathway.due} />
                            ) : (
                                <Bullet isOpenDueDate value={data.pathway.due} />
                            )}
                        </div>
                        {data.pathway.duration > 0 && (
                            <>
                                <h6 className="mt20">{text.side.time}</h6>
                                <div>
                                    <Bullet isDuration value={data.pathway.duration / 60} />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {data.steps.length > 0 && (
                <div className="mt30">
                    <Subheader title={text.steps} count={data.steps.length} />
                    <div className="one-col">
                        {data.steps.map(step => {
                            const content = (
                                <div className="prism-content">
                                    <div className="flex flex-center">
                                        <Bullet type="objective" subType={step.type} />
                                        <h4 className="ml10 mr10 flex-grow">{step.name}</h4>
                                        {step.duration > 0 && !step.completed && (
                                            <Bullet isDuration value={step.duration / 60} />
                                        )}
                                        {step.completed && <Bullet isStatus value="completed" />}
                                    </div>
                                    {step.description && <p className="truncate-4">{step.description}</p>}
                                    {!managerView && (
                                        <div>
                                            <button className="btn btn-primary">
                                                {step.completed
                                                    ? text.buttons.review
                                                    : step.progress
                                                      ? text.buttons.resume
                                                      : text.buttons.start}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );

                            if (managerView) {
                                return (
                                    <div
                                        data-testid="step-item"
                                        key={`step-${step.type}-${step.id}`}
                                        className="prism-content-box mt10"
                                    >
                                        {content}
                                    </div>
                                );
                            }

                            return step.course_Url ? (
                                <a
                                    data-testid="step-item"
                                    key={`step-${step.type}-${step.id}`}
                                    href={step.course_Url}
                                    className="prism-content-box mt10"
                                >
                                    {content}
                                </a>
                            ) : (
                                <Link
                                    data-testid="step-item"
                                    key={`step-${step.type}-${step.id}`}
                                    className="prism-content-box mt10"
                                    to={`/objectives/${user.id}/objective/${step.profile_Task_Id}`}
                                >
                                    {content}
                                </Link>
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default PathwayDetails;
